import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import moment from 'moment'
import Box from '@mui/material/Box'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import BlockIcon from '@mui/icons-material/Block'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import PoweredByFooter from '../../pages/applications/referenceRequest/Footer'
import { verifyCertificate } from '../helpers/trainingApi'

// Public certificate check, opened from the QR code on a certificate.
// Rendered by PublicRouter (signed out) and by the app router (signed in),
// so it draws its own card and relies on nothing but the tenant settings.
// It shows only what is printed on the certificate.

const formatDate = (value) => (value ? moment(value).format('D MMMM YYYY') : '')

const VIEWS = {
  valid: { Icon: CheckCircleIcon, tone: 'success', title: 'This certificate is valid' },
  expired: { Icon: WarningAmberIcon, tone: 'warning', title: 'This certificate has expired' },
  revoked: { Icon: BlockIcon, tone: 'error', title: 'This certificate has been withdrawn' },
  sample: {
    Icon: WarningAmberIcon,
    tone: 'warning',
    title: 'This is a sample certificate and is not valid evidence of training',
  },
  notFound: {
    Icon: HelpOutlineIcon,
    tone: 'error',
    title: 'We could not find a certificate with this code',
    body: 'Check that the web address matches the one printed on the certificate, or scan its QR code again.',
  },
  throttled: {
    Icon: HourglassEmptyIcon,
    tone: 'warning',
    title: 'Too many checks from this connection',
    body: 'Please wait a few minutes and try again.',
  },
  error: {
    Icon: ErrorOutlineIcon,
    tone: 'error',
    title: 'We could not check this certificate',
    body: 'Something went wrong. Please try again in a few minutes.',
  },
}

// Dark enough for 4.5:1 on the tinted panel.
const TONE_TEXT = { success: '#1B5E20', warning: '#7A4100', error: '#B42318' }
const TONE_BG = { success: '#EDF7ED', warning: '#FFF4E5', error: '#FDEDED' }

const Detail = ({ label, children }) =>
  children ? (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography component="dt" sx={{ width: { xs: '100%', sm: 180 }, color: 'text.secondary', fontSize: '1rem' }}>
        {label}
      </Typography>
      <Typography
        component="dd"
        sx={{ m: 0, flex: 1, minWidth: 0, fontWeight: 700, fontSize: '1rem', overflowWrap: 'anywhere' }}
      >
        {children}
      </Typography>
    </Box>
  ) : null

const VerifyCertificate = () => {
  const { verificationId } = useParams()

  // 'checking' or a key of VIEWS
  const [view, setView] = useState('checking')
  const [certificate, setCertificate] = useState(null)

  useEffect(() => {
    let cancelled = false
    setView('checking')
    setCertificate(null)
    verifyCertificate(verificationId)
      .then((res) => {
        if (cancelled) return
        const state = res?.data?.state
        if (res?.status === 429 || state === 'throttled') return setView('throttled')
        if (res?.status !== 200) return setView('error')
        if (state === 'notFound') return setView('notFound')
        if (state !== 'found' || !res.data.certificate) return setView('error')
        setCertificate(res.data.certificate)
        // A sample is never valid evidence, whatever its dates say.
        if (res.data.certificate.status === 'revoked') return setView('revoked')
        if (res.data.certificate.isSample) return setView('sample')
        return setView(res.data.certificate.status === 'expired' ? 'expired' : 'valid')
      })
      .catch(() => {
        if (!cancelled) setView('error')
      })
    return () => {
      cancelled = true
    }
  }, [verificationId])

  const current = VIEWS[view]
  const expiredBody =
    view === 'expired' && certificate
      ? `It was completed on ${formatDate(certificate.completedOn)} and was valid until ${formatDate(certificate.expiresOn)}.`
      : null

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, py: { xs: 2, sm: 4 }, textAlign: 'left' }}
    >
      <Paper
        variant="outlined"
        sx={{ width: '100%', maxWidth: 640, p: { xs: 2, sm: 4 }, borderRadius: 3, boxSizing: 'border-box' }}
      >
        <Typography component="h1" sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 2 }}>
          Certificate check
        </Typography>

        <Box aria-live="polite">
          {view === 'checking' ? (
            <Box role="status">
              <Typography sx={{ mb: 1, fontSize: '1rem' }}>Checking this certificate…</Typography>
              <LinearProgress />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start',
                p: 2,
                borderRadius: 2,
                border: '2px solid',
                borderColor: TONE_TEXT[current.tone],
                bgcolor: TONE_BG[current.tone],
                color: TONE_TEXT[current.tone],
              }}
            >
              <current.Icon aria-hidden="true" sx={{ fontSize: 36, flexShrink: 0 }} />
              <Box>
                <Typography component="h2" sx={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.3 }}>
                  {current.title}
                </Typography>
                {(current.body || expiredBody) && (
                  <Typography sx={{ mt: 0.5, fontSize: '1rem', color: 'text.primary' }}>
                    {current.body || expiredBody}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {certificate && (
          <>
            <Typography component="h2" sx={{ fontSize: '1.125rem', fontWeight: 700, mt: 3, mb: 0.5 }}>
              Certificate details
            </Typography>
            <Box component="dl" sx={{ m: 0 }}>
              <Detail label="Name">{certificate.learnerName}</Detail>
              <Detail label="Learner's organisation">{certificate.organisation}</Detail>
              <Detail label="Course developed by">AmbuNet (Care Nav Ltd)</Detail>
              <Detail label="Course">
                {certificate.courseTitle}
                {certificate.courseCode ? ` (${certificate.courseCode})` : ''}
              </Detail>
              <Detail label="Pathway">{certificate.pathwayTitle}</Detail>
              <Detail label="Course version">{certificate.courseVersion}</Detail>
              <Detail label="Completed on">{formatDate(certificate.completedOn)}</Detail>
              <Detail label="Valid until">{formatDate(certificate.expiresOn) || 'No expiry date'}</Detail>
              {/* Only ever claimed for a course version CPD UK has certified. */}
              {certificate.accredited === true && (
                <Detail label="Accreditation">CPD Certified by The CPD Certification Service</Detail>
              )}
            </Box>
          </>
        )}

        <Typography sx={{ mt: 3, fontSize: '1rem', color: 'text.secondary', overflowWrap: 'anywhere' }}>
          Verification ID: {String(verificationId ?? '').toUpperCase()}
        </Typography>
      </Paper>
      <PoweredByFooter />
    </Box>
  )
}

export default VerifyCertificate
